import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import { useGetBankList } from '@nxsys/hooks/api/bank';
import { useGetCompanyDraftBankNames } from '@nxsys/hooks/api/companyDraft/useCompanyDraftApi';
import { Option } from '@nxsys/types/models/Common';
import { Icons } from '@nxsys/types/icons';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useEffect, useImperativeHandle, useMemo } from 'react';
import StepTitle from './StepTitle';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

export type BankListItem = {
  bankName: Option<string> | null;
  accountName: string;
  accountNumber: string;
  sortCode: string;
  isPrimary: boolean;
  IBANReference?: string;
  bankReference?: string;
  BIC?: string;
  orgId?: string;
};

export type BankListValues = {
  banksList: BankListItem[];
};

export type BankListFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => BankListValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<BankListFormRef>;
  initialValues: Partial<BankListValues>;
  stepNumber: number;
  token?: string;
};

const BankList: React.FC<Props> = ({ formRef, initialValues, stepNumber, token }) => {
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const {
    run: fetchBankList,
    value: bankListValue,
    loading: isFetchingBankList,
  } = useGetBankList();
  const {
    run: fetchDraftBankNames,
    value: draftBankNamesValue,
    loading: isFetchingDraftBankNames,
  } = useGetCompanyDraftBankNames();
  const bankList = useMemo(
    () => (token ? draftBankNamesValue?.data : bankListValue?.data),
    [token, draftBankNamesValue, bankListValue],
  );
  const isFetchingBanks = token ? isFetchingDraftBankNames : isFetchingBankList;
  const bankNameOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    bankList?.forEach((bank) => {
      if (bank.bankName && !seen.has(bank.bankName)) {
        seen.add(bank.bankName);
        options.push({ label: bank.bankName, value: bank.bankName });
      }
    });
    return options;
  }, [bankList]);

  useEffect(() => {
    if (token) {
      fetchDraftBankNames(token);
    } else {
      fetchBankList({ page: 0, size: 999 });
    }
  }, [token]);

  const [form] = Form.useForm<BankListValues>();
  const watchBanksList = Form.useWatch('banksList', form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleSetPrimary = (index: number) => {
    const currentList = form.getFieldValue('banksList') as BankListItem[];
    const updated = currentList.map((item, i) => ({
      ...item,
      isPrimary: i === index,
    }));
    form.setFieldValue('banksList', updated);
  };

  return (
    <div className="BankList">
      <StepTitle
        step={stepNumber}
        title="Bank Accounts"
        description={isBureau ? 'Set up bank accounts for your employer' : 'Set up bank accounts for your company'}
      />

      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            banksList: [
              {
                bankName: null,
                accountName: '',
                accountNumber: '',
                sortCode: '',
                isPrimary: true,
              },
            ],
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Form.List name="banksList">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  const bankNameValue =
                    (watchBanksList?.[index]?.bankName as Option<string> | null)
                      ?.value ?? '';
                  // NX-5464: AIB SEPA files also need the employer BIC and
                  // originator ID — require them at creation time (the IBAN is
                  // required for every Irish bank). Fields only render for
                  // Ireland, so no country check here.
                  const isAibSelected = bankNameValue
                    ?.toLowerCase()
                    .includes('aib');
                  const isPrimary = watchBanksList?.[index]?.isPrimary ?? false;
                  const canDelete = (watchBanksList?.length ?? 0) > 1;

                  return (
                    <div
                      key={field.key}
                      className="border border-neutral-200 rounded-[8px] p-[16px] mb-[12px]"
                    >
                      <div className="flex items-start gap-x-[12px]">
                        <div className="flex-1">
                          <Row gutter={[16, 0]}>
                            <Col span={12}>
                              <Form.Item
                                label="Bank Name"
                                name={[field.name, 'bankName']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select a bank name',
                                  },
                                ]}
                              >
                                <AppSelect
                                  options={bankNameOptions}
                                  placeholder="Bank Name"
                                  allowClear
                                  loading={isFetchingBanks}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              {/* NX-5841: Ireland pays by IBAN, so Account
                                  Name + IBAN are the mandatory pair there and
                                  sort code / account number become optional. */}
                              <Form.Item
                                label="Account Name"
                                name={[field.name, 'accountName']}
                                rules={[
                                  {
                                    required: isIreland,
                                    message: 'Please enter account name',
                                  },
                                ]}
                              >
                                <AppInput placeholder="Account Name" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Account Number (8 Digits)"
                                name={[field.name, 'accountNumber']}
                                rules={[
                                  {
                                    required: !isIreland,
                                    message: 'Please enter account number',
                                  },
                                  {
                                    pattern: /^\d{8}$/,
                                    message: 'Account number must be 8 digits',
                                  },
                                ]}
                                normalize={(value: string) =>
                                  value.replace(/[^0-9]/g, '')
                                }
                              >
                                <AppInput
                                  placeholder="Account Number (8 Digits)"
                                  maxLength={8}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Sort Code (6 Digits)"
                                name={[field.name, 'sortCode']}
                                rules={[
                                  {
                                    required: !isIreland,
                                    message: 'Please enter sort code',
                                  },
                                  {
                                    pattern: /^\d{6}$/,
                                    message: 'Sort code must be 6 digits',
                                  },
                                ]}
                                normalize={(value: string) =>
                                  value.replace(/[^0-9]/g, '')
                                }
                              >
                                <AppInput
                                  placeholder="Sort Code (6 Digits)"
                                  maxLength={6}
                                />
                              </Form.Item>
                            </Col>

                            {isIreland && (
                              <>
                                <Col span={12}>
                                  <Form.Item
                                    label="IBAN Reference"
                                    name={[field.name, 'IBANReference']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Please enter IBAN Reference',
                                      },
                                    ]}
                                  >
                                    <AppInput
                                      placeholder="IBAN Reference"
                                      maxLength={30}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Bank Reference"
                                    name={[field.name, 'bankReference']}
                                  >
                                    <AppInput placeholder="Bank Reference" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="BIC"
                                    name={[field.name, 'BIC']}
                                    rules={[
                                      {
                                        required: isAibSelected,
                                        message: 'Please enter BIC',
                                      },
                                    ]}
                                  >
                                    <AppInput placeholder="BIC" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Originator ID"
                                    name={[field.name, 'orgId']}
                                    rules={[
                                      {
                                        required: isAibSelected,
                                        message: 'Please enter originator ID',
                                      },
                                      {
                                        pattern:
                                          /^IE\d{2}(SCT|MCY|SDD|XXX)\d{6}$/,
                                        message:
                                          'Originator ID must be a valid IEAABBBCCCCCC (e.g. IE94SCT833215)',
                                      },
                                    ]}
                                  >
                                    <AppInput
                                      placeholder="Originator ID (e.g. IE94SCT833215)"
                                      maxLength={13}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            )}
                          </Row>

                          <div className="flex items-center gap-x-[8px] mt-[4px]">
                            <AppSwitch
                              checked={isPrimary}
                              onChange={() => {
                                if (isPrimary) return;
                                handleSetPrimary(index);
                              }}
                              disabled={watchBanksList?.length === 1}
                            />
                            <span
                              className={`cursor-pointer text-[14px] ${
                                isPrimary
                                  ? 'text-primary-600 font-medium'
                                  : 'text-neutral-500'
                              }`}
                              onClick={() => {
                                if (isPrimary) return;
                                handleSetPrimary(index);
                              }}
                            >
                              Primary
                            </span>
                          </div>
                        </div>

                        <div
                          className={`mt-[4px] ${
                            canDelete
                              ? 'cursor-pointer'
                              : 'opacity-30 cursor-not-allowed'
                          }`}
                          onClick={() => {
                            if (!canDelete) return;
                            remove(field.name);
                          }}
                        >
                          <Icons.IconTrash />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <AppButton
                  variant="outlined"
                  leftIcon={<Icons.IconPlus />}
                  name="Add another bank account"
                  onClick={() =>
                    add({
                      bankName: null,
                      accountName: '',
                      accountNumber: '',
                      sortCode: '',
                      isPrimary: false,
                    })
                  }
                />
              </>
            )}
          </Form.List>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default BankList;
